import {
  COLOR_LABEL,
  StockMode,
  StockStatus,
  StockNotifications,
  DoubleDealItems,
  SmartphonesLabel
} from "../config/constants";
import labels from "../config/labels";

export const createColorSelectionConfigs = (bundleOptions = [], productSelections = []) => {
  const products = [];

  let colorSelectionCount = 0;

  bundleOptions.forEach((option) => {
    const productId = option?.product.id;
    const productCount = productSelections.reduce((total, ps) => total + (ps?.productId === productId), 0);

    const configOpts = option?.product?.configurable_options;
    const attrId = Array.isArray(configOpts)
      ? configOpts.find((option) => option.label === COLOR_LABEL)?.attribute_id
      : '';

    if (productCount === 0) {
      return;
    } else if (productCount === 1) {
      colorSelectionCount++;

      const prodSelection = productSelections.find(ps => ps?.productId === productId);

      products.push({
        id: option.id,
        value_string: prodSelection?.product?.id,
        attribute_id: attrId || "",
        attribute_value: prodSelection.value_index,
      });
    } else {
      const doubleDevices = productSelections
        .filter(ps => ps?.productId === productId)
        .map((prodSelection) => {
          colorSelectionCount++;

          return {
            id: option.id,
            value_string: prodSelection?.product?.id,
            attribute_id: attrId || "",
            attribute_value: prodSelection.value_index,
          };
        })

      products.push(...doubleDevices);
    }

  });

  return { products, count: colorSelectionCount };
}

/**
 * Method to create cart items array.
 * @param {Object} params - an object with dealDetails, buyOptionTypeId, vasList
 * @param {Object} params.dealDetails - a deal details object.
 * @param {string} params.buyOptionTypeId - a string Id for the selected application type.
 * @param {Array<Object>} params.vasList - an array of VAS added.
 * @returns {Array<Object>} cart array with list of user selections.
 */
export const createCartItems = ({ dealDetails, buyOptionTypeId, vasList, cartBundleOptions }) => {
  if (!dealDetails
    || !dealDetails?.sku
    || !Array.isArray(dealDetails?.items)
    || !Array.isArray(cartBundleOptions)
  ) {
    return [];
  }

  // Add primary deal options
  const dealSKU = dealDetails?.sku;

  const buyOptionId = dealDetails?.options?.[0]?.option_id;
  const customizableOptions = buyOptionId && buyOptionTypeId
    ? [{
      id: buyOptionId,
      value_string: buyOptionTypeId?.toString(),
    }]
    : [];

  let cartArr = [
    {
      bundle_options: cartBundleOptions,
      data: {
        sku: `${dealSKU}`.toString(),
        quantity: labels.lbl_cart_min_product_add_qty,
      },
      customizable_options: customizableOptions,
    },
  ];

  // Add VAS list to cart list.
  if (Array.isArray(vasList) && vasList.length) {
    const vasArray = vasList.map((item) => ({
      data: {
        sku: item.id,
        quantity: 1,
      },
      parent_product_sku: `${dealSKU}`.toString(),
      monthly_recurring: +item.price,
      customizable_options: customizableOptions,
      bundle_options: [],
    }));

    cartArr.push(...vasArray);
  }

  return cartArr;
}

/**
 * Method to create a list of selected product colors
 *
 * @returns {Array<ColorSelections>} a list of selected primary, secondary and inclusion color
 * selections
 */
export const createColorSelections = ({ selectedPrimaryDeviceColor, selectedSecondaryDeviceColor, dealInclusionColors }) => {
  const productSelections = [];

  if (selectedPrimaryDeviceColor && selectedPrimaryDeviceColor?.productId) {
    productSelections.push(selectedPrimaryDeviceColor);
  }

  if (selectedSecondaryDeviceColor && selectedSecondaryDeviceColor?.productId) {
    productSelections.push(selectedSecondaryDeviceColor);
  }
  if (Object.values(dealInclusionColors).length > 0) {
    const selectedIncColors = Object.values(dealInclusionColors).map(sic => sic.selectedColor);

    productSelections.push(...selectedIncColors)
  }

  return productSelections;
}

/**
 * @typedef {Object} ColorOption
 * @property {number} id - unique id
 * @property {string} label - name of color
 * @property {ColorNotification} notification - optional notification object with "text", "description", "type"
 * @property {Object} product - product device object.
 * @property {string} value - hex value color. e.g. "#ffffff".
 * @property {number} value_index - unique id
 */

/**
 * getFormattedColorOptions - creates an array of colors with product associated with the color
 * @param {Array} configurable_options - list of configurable options (must have "Colour" as one of the options)
 * @param {Array} variants - list of product variants. Each variant is associated with a specific color.
 * @template {Object<string, ColorOption>} T
 * @returns {Array<T>} - array of colors
 */
const getFormattedColorOptions = (configurable_options, variants = [], productId = 0) => {
  if (!Array.isArray(configurable_options)) return [];

  const colors = configurable_options
    .find((option) => option.label === COLOR_LABEL);

  const colorOptions = Array.isArray(colors.values)
    ? colors.values.map((val) => ({
      id: val.value_index,
      label: val.label,
      product: null,
      productId,
      value: val.swatch_data?.value,
      value_index: val.value_index,
      mode: StockMode.IN_STOCK,
    }))
    : [];

  if (colorOptions.length > 0 && Array.isArray(variants)) {
    colorOptions.forEach(cOption => {
      const product = variants.find(variant =>
        variant.attributes.some(attr => attr.value_index === cOption.value_index)
      );

      cOption.product = product?.product || null;
      cOption.isAvailable = typeof product?.product?.stock_status === "string"
        && product?.product?.stock_status !== StockStatus.OUT_OF_STOCK;

      if (!product?.product || product?.product?.stock_status === StockStatus.OUT_OF_STOCK) {
        cOption.notification = StockNotifications.OutOfStock;
        cOption.mode = StockMode.OUT_OF_STOCK;
      } else if (product?.product?.stock_status === StockStatus.IN_STOCK && product?.product?.quantity <= 0) {
        cOption.notification = StockNotifications.BackOrder;
        cOption.mode = StockMode.BACK_ORDER;
      } else {
        cOption.notification = null;
      }
    });
  }

  return colorOptions;
}

/**
 * Method to extract list of color options from device details object.
 * @param {Object} deviceDetails
 * @returns {Array<ColorOption>} list of device color options
 */
export const extractDeviceColorOptions = (deviceDetails) => {

  if (typeof deviceDetails !== "object" || !Array.isArray(deviceDetails?.configurable_options))
    return [];

  return getFormattedColorOptions(deviceDetails?.configurable_options, deviceDetails?.variants, deviceDetails.id);
}

/**
 * Extract and formats color options from each deal inclusion.
 * @param {Array<Object>} inclusions - list of deal inclusions.
 * @returns {Object} map of sku and inclusion color details
 */
export const extractInclusionColorOptions = (inclusions) => {
  const dealInclusionColors = {};

  if (!Array.isArray(inclusions)) return dealInclusionColors;

  inclusions.forEach((item, index) => {
    if (
      !Array.isArray(item?.configurable_options)
      || !item?.configurable_options.length
    ) return;

    const colorOptions = getFormattedColorOptions(item?.configurable_options, item.variants, item.id);

    if (!colorOptions.length) return;

    dealInclusionColors[item.sku + index] = {
      options: colorOptions,
      selectedColor: selectInitialColor(colorOptions),
      groupId: item.sku
    };
  });

  return dealInclusionColors
}

/**
 * Method to extract and structure all products under a deal into a single list of products
 * @param {Object} activeContract - active deal contract
 * @returns {Array} list of all products included in contract deal
 */
export const extractProductList = (activeContract = {}) => {

  if (!Array.isArray(activeContract?.items) || !activeContract?.items?.length)
    return [];

  const allProductOptions = activeContract?.items.map((item) => {
    return item.options.reduce((productAcc, prod) => {
      if (prod?.is_hidden_product === "1") {
        return productAcc;
      }

      const isSecondary = item.title === SmartphonesLabel && !prod.is_default;

      const product = {
        ...prod.product,
        type: item.title,
        isPrimary: prod.is_default,
        isSecondary: isSecondary,
        optionQuantity: prod.quantity,
        optionId: prod.id,
        dealSku: item.sku
      };

      return [...productAcc, product];
    }, [])
  });

  return Array.isArray(allProductOptions) ? allProductOptions.flat() : [];
}

export const isDoubleDealDevice = (allProductsUnderContract = []) => {
  const doubleDeals = allProductsUnderContract.filter(item => DoubleDealItems.includes(item.type));

  return doubleDeals.length > 1 || doubleDeals.some(dd => dd.optionQuantity > 1);
}

/**
 *
 * @param {ColorOption} deviceColor - color object
 * @returns {boolean} boolean value indicating whether deviceColor param is valid or not.
 */
export const isValidDeviceColor = (deviceColor) => !!(
  !!deviceColor
  && typeof deviceColor === "object"
  && deviceColor.id
  && deviceColor.label
);

export const isValidRouterResponse = (routerRes) => !!(
  routerRes
  && Array.isArray(routerRes?.products?.items)
  && routerRes?.products?.items.length > 0
);

/**
 * Method to select an initial color from a list of colors.
 * Returns the first IN_STOCK color. If no color is IN_STOCK, returns back order color
 * @param {Array<ColorOption>} colors - list of colors to select from
 * @returns {ColorOption} device color object.
 */
export const selectInitialColor = (colors = []) => {
  if (!Array.isArray(colors) || colors.length === 0) return null;

  if (colors.length === 1) return colors[0];

  let selectedColor = colors.find(prodColor => prodColor.isAvailable
    && prodColor.product?.quantity > 0
  );

  if (selectedColor)
    return selectedColor;

  return colors.find(prodColor => prodColor.isAvailable);
}
